/**
 * O assistente, do lado da casca.
 *
 * ADR-0003 em codigo: interpretar e propor **nunca** escreve nada. So `confirm()`
 * despacha, e `confirm()` so e chamado por um toque explicito da pessoa. Nao ha
 * caminho nenhum de `interpret()` para um repositorio — e o invariante que o
 * teste `assistant.test.tsx` ataca de propósito.
 *
 * A casca tambem nao sabe o que e uma despesa nem um medicamento (regra 1):
 * descobre quem trata de uma intencao pelo registro de plugins e entrega-lhe a
 * acao pelo servico que esse plugin publicou.
 */
import { AiError, CoreEvents, type AiAction, type IntentDescriptor } from '@flos/shared';
import type { FlosCore } from '@flos/core';
import {
  intentServiceId,
  type AiModulePort,
  type IntentOutcome,
  type IntentRouterLike,
  type PluginIntentHandler,
} from './ports';

export const AI_SETTINGS_NAMESPACE = 'core';
export const AI_KEY_SETTING = 'ai.apiKey';

export interface Assistant {
  /** `false` quando falta o pacote de IA ou a chave. `reason` explica porque. */
  readonly available: boolean;
  readonly unavailableReason: string | null;
  /** Intencoes que este usuário pode mesmo pedir. */
  intents(): IntentDescriptor[];
  /** Interpreta. NUNCA escreve. Devolve uma proposta por confirmar. */
  interpret(text: string, options?: { signal?: AbortSignal }): Promise<AiAction>;
  /** Aplica uma acao ja confirmada por um humano. */
  confirm(action: AiAction): Promise<IntentOutcome>;
  /** A pessoa corrigiu ou cancelou: sinal de aprendizagem, sem escrita. */
  reject(action: AiAction, correction?: string): void;
}

/** Assistente que so sabe dizer, em portugues, porque e que nao esta disponivel. */
export function unavailableAssistant(reason: string): Assistant {
  return {
    available: false,
    unavailableReason: reason,
    intents: () => [],
    async interpret() {
      throw new AiError(reason, 'unsupported', false);
    },
    async confirm() {
      throw new AiError(reason, 'unsupported', false);
    },
    reject() {
      /* nada a aprender sem assistente */
    },
  };
}

export interface CreateAssistantOptions {
  core: FlosCore;
  module: AiModulePort | null;
  /** Chave dada nas configuracoes. Nunca vem do bundle. */
  apiKey?: string | undefined;
  /** Injectavel nos testes, para nao precisar do pacote real. */
  router?: IntentRouterLike;
}

export function createAssistant(options: CreateAssistantOptions): Assistant {
  const { core } = options;

  let router: IntentRouterLike | null = options.router ?? null;
  let unavailableReason: string | null = null;

  if (!router) {
    if (!options.module) {
      unavailableReason =
        'O assistente ainda não está instalado nesta versão do aplicativo.';
    } else if (!options.apiKey) {
      unavailableReason =
        'Falta a chave do assistente. Pode colá-la em Configurações, ou continuar a registrar as coisas à mão.';
    } else {
      try {
        const provider = options.module.createAiProvider({ apiKey: options.apiKey });
        router = new options.module.IntentRouter({
          provider,
          intents: core.registry.intents(),
          can: (scope: string) => core.session.can(scope),
          logger: core.logger,
        });
      } catch (error) {
        unavailableReason =
          error instanceof AiError
            ? error.userMessage
            : 'Não consegui preparar o assistente. Pode registrar as coisas à mão.';
      }
    }
  }

  if (!router) {
    return unavailableAssistant(unavailableReason ?? 'O assistente não está disponível.');
  }

  const active = router;

  /** Quem trata desta intencao. Vem do registro, nao de um `if` por dominio. */
  function ownerOf(intentName: string): { pluginId: string; pluginName: string } | null {
    const registered = core.registry.intents().find((intent) => intent.name === intentName);
    if (!registered) return null;
    const manifest = core.registry.entry(registered.pluginId)?.manifest;
    return {
      pluginId: registered.pluginId,
      pluginName: manifest?.name ?? registered.pluginId,
    };
  }

  return {
    available: true,
    unavailableReason: null,

    intents: () => core.registry.intents(),

    async interpret(text, interpretOptions) {
      // As intencoes mudam quando um plugin arranca tarde ou muda de permissoes.
      active.setIntents(core.registry.intents());
      const action = await active.route(text, {
        ...(interpretOptions?.signal ? { signal: interpretOptions.signal } : {}),
      });
      // Proposto, nao aplicado. Quem ouvir este evento nao deve escrever nada.
      core.events.emit(CoreEvents.AI_ACTION_PROPOSED, action);
      return action;
    },

    async confirm(action) {
      const owner = ownerOf(action.intent);
      if (!owner) {
        return {
          ok: false,
          message: 'Já não sei quem trata desse pedido. Tente registrar à mão, por favor.',
        };
      }

      const handler = core.registry.services.consume<PluginIntentHandler>(
        intentServiceId(owner.pluginId),
      );

      // O evento sai sempre: e a via de quem prefere ouvir a publicar servico.
      core.events.emit(CoreEvents.AI_ACTION_CONFIRMED, action);

      if (!handler || typeof handler.handle !== 'function') {
        return {
          ok: false,
          message: `${owner.pluginName} ainda não sabe concluir este pedido sozinho. Pode fazê-lo à mão.`,
        };
      }

      try {
        const outcome = await handler.handle(action);
        return { ok: true, ...outcome };
      } catch (error) {
        core.logger.error('O plugin falhou a aplicar uma acao confirmada', {
          plugin: owner.pluginId,
          intencao: action.intent,
          erro: error instanceof Error ? error.message : String(error),
        });
        return {
          ok: false,
          message:
            error instanceof AiError
              ? error.userMessage
              : `Não consegui concluir em ${owner.pluginName}. Nada foi alterado.`,
        };
      }
    },

    reject(action, correction) {
      core.events.emit(CoreEvents.AI_ACTION_REJECTED, { action, correction });
      core.logger.info('Proposta do assistente recusada', {
        intencao: action.intent,
        confianca: action.confidence,
        ...(correction ? { correccao: correction } : {}),
      });
    },
  };
}
